<template>
  <div class="login-page">
    <div class="login-container">
      <div class="login-header">
        <h1 class="text-xl font-bold">托管中心管理系统</h1>
        <p class="text-sm mt-1 opacity-80">请登录您的账号</p>
      </div>
      
      <div class="login-form">
        <!-- 系统类型选择 -->
        <div class="mb-6">
          <h2 class="text-gray-700 font-medium mb-3">选择系统</h2>
          <div class="system-selector">
            <div 
              :class="['system-option', { active: !isQuestionBankSystem }]"
              @click="selectSystem('normal')"
            >
              <i class="fas fa-school"></i>
              <span>托管系统</span>
            </div>
            <div 
              :class="['system-option', { active: isQuestionBankSystem }]"
              @click="selectSystem('question-bank')"
            >
              <i class="fas fa-book"></i>
              <span>题库系统</span>
            </div>
          </div>
        </div>
        
        <!-- 角色选择 -->
        <div class="mb-6">
          <h2 class="text-gray-700 font-medium mb-3">选择角色</h2>
          <div class="grid grid-cols-4 gap-2" v-if="!isQuestionBankSystem">
            <button 
              v-for="role in normalRoles" 
              :key="role.id"
              :id="`${role.id}-role`"
              :class="['role-btn flex flex-col items-center justify-center p-3 rounded-lg border border-gray-200 text-gray-600', 
                      selectedRole === role.id ? 'active' : '']"
              @click="selectRole(role.id)"
            >
              <i :class="['fas', role.icon, 'role-icon']"></i>
              <span class="text-xs">{{ role.label }}</span>
            </button>
          </div>
          <div class="grid grid-cols-2 gap-2" v-else>
            <button 
              v-for="role in questionBankRoles" 
              :key="role.id"
              :id="`${role.id}-role`"
              :class="['role-btn flex flex-col items-center justify-center p-3 rounded-lg border border-gray-200 text-gray-600', 
                      selectedRole === role.id ? 'active' : '']"
              @click="selectRole(role.id)"
            >
              <i :class="['fas', role.icon, 'role-icon']"></i>
              <span class="text-xs">{{ role.label }}</span>
            </button>
          </div>
        </div>
        
        <!-- 登录方式切换 -->
        <div class="login-type-selector">
          <div 
            :class="['login-type-option', { active: loginType === 'password' }]" 
            @click="loginType = 'password'"
          >
            密码登录
          </div>
          <div 
            :class="['login-type-option', { active: loginType === 'sms' }]" 
            @click="loginType = 'sms'"
          >
            短信登录
          </div>
        </div>
        
        <!-- 登录表单 -->
        <el-form
          ref="loginForm"
          v-if="loginType === 'password'"
          :model="form"
          :rules="rules"
          class="login-form"
          size="medium"
          @submit.native.prevent="handleLogin"
        >
          <el-form-item prop="username">
            <label class="block text-gray-700 text-sm font-medium mb-2">
              手机号/账号
            </label>
            <div class="relative">
              <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                <i class="fas fa-user"></i>
              </span>
              <el-input 
                v-model="form.username" 
                placeholder="请输入手机号或账号"
                class="w-full pl-10"
              />
            </div>
          </el-form-item>
          
          <el-form-item prop="password">
            <label class="block text-gray-700 text-sm font-medium mb-2">
              密码
            </label>
            <div class="relative">
              <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                <i class="fas fa-lock"></i>
              </span>
              <el-input 
                v-model="form.password" 
                :type="passwordVisible ? 'text' : 'password'" 
                placeholder="请输入密码"
                class="w-full pl-10"
              />
              <span 
                class="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer text-gray-400"
                @click="passwordVisible = !passwordVisible"
              >
                <i :class="['fas', passwordVisible ? 'fa-eye-slash' : 'fa-eye']"></i>
              </span>
            </div>
          </el-form-item>
          
          <div class="flex items-center justify-between mb-6">
            <div class="flex items-center">
              <el-checkbox v-model="form.remember">记住我</el-checkbox>
            </div>
          </div>
          
          <el-button 
            type="primary" 
            class="w-full"
            :loading="loading" 
            @click="handleLogin"
          >
            登录
          </el-button>
        </el-form>
        
        <!-- 短信验证码登录表单 -->
        <el-form
          ref="smsForm"
          v-else
          :model="smsForm"
          :rules="smsRules"
          class="login-form"
          size="medium"
          @submit.native.prevent="handleSmsLogin"
        >
          <el-form-item prop="phone">
            <label class="block text-gray-700 text-sm font-medium mb-2">
              手机号
            </label>
            <div class="relative">
              <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                <i class="fas fa-mobile-alt"></i>
              </span>
              <el-input 
                v-model="smsForm.phone" 
                placeholder="请输入手机号"
                class="w-full pl-10"
              />
            </div>
          </el-form-item>
          
          <el-form-item prop="smsCode">
            <label class="block text-gray-700 text-sm font-medium mb-2">
              验证码
            </label>
            <div class="relative flex items-center">
              <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                <i class="fas fa-key"></i>
              </span>
              <el-input 
                v-model="smsForm.smsCode" 
                placeholder="请输入验证码"
                class="w-full pl-10"
              />
              <el-button 
                type="primary"
                class="ml-2 flex-shrink-0"
                :disabled="cooldown > 0"
                @click="sendCode"
              >
                {{ cooldown > 0 ? `${cooldown}秒` : '获取验证码' }}
              </el-button>
            </div>
          </el-form-item>
          
          <div class="flex items-center justify-between mb-6">
            <div class="flex items-center">
              <el-checkbox v-model="smsForm.remember">记住我</el-checkbox>
            </div>
          </div>
          
          <el-button 
            type="primary" 
            class="w-full"
            :loading="smsLoading"
            @click="handleSmsLogin"
          >
            登录
          </el-button>
        </el-form>
        
        <div class="mt-6 text-center">
          <p class="text-sm text-gray-600">
            还没有账号? 
            <router-link :to="`/${$route.params.schoolCode}/register`" class="text-indigo-600 hover:text-indigo-800">
              立即注册
            </router-link>
          </p>
        </div>
      </div>
    </div>
    
    <!-- 更新通知对话框 -->
    <el-dialog
      title="家长登录指南"
      :visible.sync="updateNoticeVisible"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      width="85%"
      center
      class="update-notice-dialog"
    >
      <div class="update-notice-content">
        <div class="notice-icon">
          <i class="el-icon-user-solid"></i>
        </div>
        <h3 class="notice-title">欢迎家长登录系统</h3>
        <div class="notice-details">
          <p>尊敬的家长，感谢您使用芮特成长中心管理系统：</p>
          <ul>
            <li>
              <i class="el-icon-warning" style="color: #F56C6C;"></i> <strong>未注册家长</strong>：请先点击下方"立即注册"按钮完成注册
            </li>
            <li>
              <i class="el-icon-success"></i> <strong>已注册家长</strong>：请在登录界面选择"家长"身份后进行登录
            </li>
          </ul>
          <p class="notice-warning" style="color: #F56C6C; font-weight: bold; margin-top: 15px;">验证码功能尚未完善，请家长使用密码登陆</p>
          <p class="notice-tips">如有疑问，请联系班主任获取帮助</p>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="acknowledgeUpdate">我已知晓</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { sendSmsCode } from '@/api/auth';

export default {
  name: 'Login',
  data() {
    return {
      selectedRole: 'admin',
      passwordVisible: false,
      loading: false,
      smsLoading: false,
      loginType: 'password',
      isQuestionBankSystem: false,
      updateNoticeVisible: true, // 默认显示更新通知
      updateNoticeContent: {
        version: 'v1.0',
        date: '',
        title: '欢迎家长登录',
        items: [
          '未注册家长请先注册后登录',
          '已注册家长选择家长的身份进行登录'
        ]
      },
      form: {
        username: '',
        password: '',
        schoolCode: '',
        role: 'admin',
        remember: false
      },
      smsForm: {
        phone: '',
        smsCode: '',
        schoolCode: '',
        role: 'admin',
        remember: false
      },
      cooldown: 0,
      normalRoles: [
        { id: 'admin', label: '管理员', icon: 'fa-user-shield' },
        { id: 'teacher', label: '老师', icon: 'fa-chalkboard-teacher' },
        { id: 'parent', label: '家长', icon: 'fa-user-friends' },
        { id: 'student', label: '学生', icon: 'fa-user-graduate' }
      ],
      questionBankRoles: [
        { id: '6', label: '录题人员', icon: 'fa-edit' },
        { id: '7', label: '使用人员', icon: 'fa-book-reader' }
      ],
      rules: {
        username: [
          { required: true, message: '请输入手机号或账号', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' }
        ]
      },
      smsRules: {
        phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ],
        smsCode: [
          { required: true, message: '请输入验证码', trigger: 'blur' },
          { pattern: /^\d{6}$/, message: '验证码为6位数字', trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    // 每次登录都显示更新通知，不再检查localStorage
    this.updateNoticeVisible = true;
    
    // 设置更新日期为当前日期
    this.updateNoticeContent.date = this.getCurrentDate();
    
    // 从URL路径中获取学校代码
    const schoolCode = this.$route.params.schoolCode;
    if (schoolCode) {
      this.form.schoolCode = schoolCode;
      this.smsForm.schoolCode = schoolCode;
      console.log('从URL获取学校代码:', schoolCode);
    }
    
    const rememberedUsername = localStorage.getItem('remembered_username');
    if (rememberedUsername) {
      this.form.username = rememberedUsername;
      this.form.remember = true;
    }
    
    const rememberedPhone = localStorage.getItem('remembered_phone');
    if (rememberedPhone) {
      this.smsForm.phone = rememberedPhone;
      this.smsForm.remember = true;
    }
    
    const rememberedRole = localStorage.getItem('remembered_role');
    if (rememberedRole) {
      this.selectedRole = rememberedRole;
      this.form.role = rememberedRole;
      this.smsForm.role = rememberedRole;
    } else {
      // 如果没有记住的角色，明确设置为管理员
      this.selectedRole = 'admin';
      this.form.role = 'admin';
      this.smsForm.role = 'admin';
    }
    
    // 确保选中的角色按钮样式正确
    this.$nextTick(() => {
      console.log('设置默认角色:', this.selectedRole);
    });
  },
  methods: {
    getCurrentDate() {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    selectSystem(systemType) {
      this.isQuestionBankSystem = systemType === 'question-bank';
      // 切换系统时重置角色选择
      if (this.isQuestionBankSystem) {
        this.selectRole('6'); // 默认选择录题人员
      } else {
        this.selectRole('admin'); // 默认选择管理员
      }
    },
    acknowledgeUpdate() {
      // 关闭对话框并记录用户已查看
      this.updateNoticeVisible = false;
    },
    selectRole(roleId) {
      this.selectedRole = roleId;
      this.form.role = roleId;
      this.smsForm.role = roleId;
      
      console.log('已选择角色:', roleId, typeof roleId);
    },
    handleLogin() {
      this.$refs.loginForm.validate(valid => {
        if (valid) {
          this.loading = true;
          
          if (this.form.remember) {
            localStorage.setItem('remembered_username', this.form.username);
            localStorage.setItem('remembered_role', this.form.role);
          } else {
            localStorage.removeItem('remembered_username');
          }
          
          // 记录当前是否是题库系统登录
          const isQuestionBank = this.isQuestionBankSystem;
          console.log('登录系统类型:', isQuestionBank ? '题库系统' : '托管系统');
          console.log('登录角色ID:', this.form.role);
          
          this.$store.dispatch('auth/login', {
            username: this.form.username,
            password: this.form.password,
            schoolCode: this.form.schoolCode,
            role: this.form.role,
            isQuestionBank: isQuestionBank  // 传递是否是题库系统
          }).then(() => {
            this.loading = false;
            
            const redirectPath = `/${this.form.schoolCode}/campus-selection`;
            this.$router.push(redirectPath);
          }).catch(error => {
            this.loading = false;
            this.$message.error(error.message || '登录失败，请重试');
          });
        }
      });
    },
    handleSmsLogin() {
      this.$refs.smsForm.validate(valid => {
        if (valid) {
          this.smsLoading = true;
          
          if (this.smsForm.remember) {
            localStorage.setItem('remembered_phone', this.smsForm.phone);
            localStorage.setItem('remembered_role', this.smsForm.role);
          } else {
            localStorage.removeItem('remembered_phone');
          }
          
          // 记录当前是否是题库系统登录
          const isQuestionBank = this.isQuestionBankSystem;
          console.log('短信登录系统类型:', isQuestionBank ? '题库系统' : '托管系统');
          console.log('短信登录角色ID:', this.smsForm.role);
          
          this.$store.dispatch('auth/smsLogin', {
            phone: this.smsForm.phone,
            smsCode: this.smsForm.smsCode,
            schoolCode: this.smsForm.schoolCode,
            role: this.smsForm.role,
            isQuestionBank: isQuestionBank  // 传递是否是题库系统
          }).then(() => {
            this.smsLoading = false;
            
            const redirectPath = `/${this.smsForm.schoolCode}/campus-selection`;
            this.$router.push(redirectPath);
          }).catch(error => {
            this.smsLoading = false;
            this.$message.error(error.message || '登录失败，请重试');
          });
        }
      });
    },
    sendCode() {
      this.$refs.smsForm.validateField('phone', (err) => {
        if (err) return;
        
        this.cooldown = 60;
        const timer = setInterval(() => {
          this.cooldown--;
          if (this.cooldown <= 0) {
            clearInterval(timer);
          }
        }, 1000);
        
        sendSmsCode({
          phone: this.smsForm.phone,
          code_type: 'login'
        })
          .then(response => {
            this.$message.success('验证码已发送，请注意查收');
            console.log('验证码发送成功:', response);
          })
          .catch(error => {
            this.$message.error(error.message || '验证码发送失败，请重试');
            console.error('验证码发送失败:', error);
            clearInterval(timer);
            this.cooldown = 0;
          });
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.login-page {
  background-color: #f9fafb;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-container {
  width: 100%;
  max-width: 380px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.login-header {
  padding: 20px;
  text-align: center;
  background: #4f46e5;
  color: white;
}

.login-form {
  padding: 24px;
}

.role-btn {
  transition: all 0.3s ease;
  
  &:hover, &.active {
    background-color: #4f46e5;
    color: white;
  }
}

.role-icon {
  font-size: 24px;
  margin-bottom: 8px;
}

.grid {
  display: grid;
}

.grid-cols-4 {
  grid-template-columns: repeat(4, 1fr);
}

.mb-6 {
  margin-bottom: 1.5rem;
}

.mb-3 {
  margin-bottom: 0.75rem;
}

.text-gray-700 {
  color: #374151;
}

.font-medium {
  font-weight: 500;
}

.gap-2 {
  gap: 0.5rem;
}

.text-gray-600 {
  color: #4B5563;
}

.rounded-lg {
  border-radius: 0.5rem;
}

.border {
  border-width: 1px;
}

.border-gray-200 {
  border-color: #E5E7EB;
}

.text-xs {
  font-size: 0.75rem;
  line-height: 1rem;
}

.mt-6 {
  margin-top: 1.5rem;
}

.text-indigo-600 {
  color: #4F46E5;
}

.text-gray-500 {
  color: #6B7280;
}

.login-type-selector {
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid #ebeef5;
  
  .login-type-option {
    flex: 1;
    text-align: center;
    padding: 10px 0;
    font-size: 14px;
    color: #606266;
    cursor: pointer;
    
    &.active {
      color: #409eff;
      font-weight: 600;
      border-bottom: 2px solid #409eff;
    }
  }
}

.system-selector {
  display: flex;
  margin-bottom: 10px;
  background-color: #f5f7fa;
  border-radius: 8px;
  overflow: hidden;
  
  .system-option {
    flex: 1;
    padding: 12px 0;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s;
    display: flex;
    flex-direction: column;
    align-items: center;
    
    i {
      font-size: 20px;
      margin-bottom: 5px;
    }
    
    &:hover {
      background-color: #e0e6ed;
    }
    
    &.active {
      background-color: #4f46e5;
      color: white;
    }
  }
}

/* 更新通知样式 */
.update-notice-dialog {
  .el-dialog__header {
    padding: 15px 20px;
    background: #409EFF;
    
    .el-dialog__title {
      color: white;
      font-weight: 600;
    }
  }
  
  .update-notice-content {
    text-align: center;
    padding: 10px 0;
    
    .notice-icon {
      font-size: 40px;
      color: #409EFF;
      margin-bottom: 10px;
    }
    
    .notice-title {
      font-size: 18px;
      font-weight: 600;
      margin-bottom: 15px;
      color: #303133;
    }
    
    .notice-details {
      text-align: left;
      padding: 0 15px;
      
      p {
        margin: 10px 0;
      }
      
      ul {
        padding-left: 10px;
        margin: 15px 0;
        
        li {
          list-style: none;
          padding: 5px 0;
          
          i {
            color: #67C23A;
            margin-right: 8px;
          }
        }
      }
      
      .notice-version {
        font-size: 14px;
        color: #606266;
        margin-top: 20px;
      }
      
      .notice-date {
        font-size: 14px;
        color: #606266;
      }
    }
  }
  
  .el-dialog__footer {
    border-top: 1px solid #EBEEF5;
    padding: 15px 20px;
  }
}

@media (max-width: 768px) {
  .update-notice-dialog {
    margin: 3vh auto !important;
    
    .update-notice-content {
      padding: 5px 0;
      
      .notice-icon {
        font-size: 30px;
      }
      
      .notice-title {
        font-size: 16px;
      }
    }
  }
}
</style>